import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { SoarQueryKeys } from '../queryKeys';
import { PlaybookDto, playbookService } from '../services/playbookService';
import { Playbook } from '../types';

export interface UsePlaybooksOptions {
  enabled?: boolean;
  playbookId?: string;
  isTriggerable?: boolean;
  entityType?: string;
}

export const usePlaybooks = (options: UsePlaybooksOptions = {}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = options.enabled ?? Object.keys(options).length === 0;
  const { isTriggerable, entityType } = options;

  const mapDtoToPlaybook = useCallback(
    (dto: PlaybookDto): Playbook => ({
      id: dto.playbookId,
      name: dto.name,
      description: dto.description,
      entityType: dto.entityType,
      owner: dto.createdByUserId || 'System',
      lastUpdate: dto.updatedAt || dto.createdAt || new Date().toISOString(),
      runs: dto.runsCount || 0,
      successFailed: `${dto.successCount || 0}/${dto.failedCount || 0}`,
      status: dto.enabled ? 'Active' : 'Inactive',
      actions: dto.actions,
      triggerable: dto.triggerable,
      editable: dto.editable,
    }),
    []
  );

  const {
    data: playbooks = [],
    isLoading,
    isFetched,
    refetch: fetchPlaybooks,
  } = useQuery({
    queryKey: [SoarQueryKeys.PLAYBOOKS, { isTriggerable, entityType }],
    queryFn: async () => {
      const data = await playbookService.getPlaybooks({ isTriggerable, entityType });
      return Array.isArray(data) ? data.map(mapDtoToPlaybook) : [];
    },
    enabled,
  });

  const deletePlaybookMutation = useMutation({
    mutationFn: (id: string) => playbookService.deletePlaybook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
    },
  });

  const duplicatePlaybookMutation = useMutation({
    mutationFn: async (id: string) => {
      const dto = await playbookService.getPlaybookById(id);
      return playbookService.createPlaybook({
        playbookName: `${dto.name} Copy`,
        playbookDescription: dto.description,
        entityType: dto.entityType,
        enabled: dto.enabled,
        manualTriggeringAllowed: dto.manualTriggeringAllowed,
        actions: dto.actions,
        usersAllowedToEdit: dto.usersAllowedToEdit,
        usersAllowedToTrigger: dto.usersAllowedToTrigger,
        allowedInApps: dto.allowedInApps,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
      enqueueSnackbar('Playbook duplicated successfully', { variant: 'success' });
    },
  });

  const addPlaybook = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
  }, [queryClient]);

  const updatePlaybook = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [SoarQueryKeys.PLAYBOOKS] });
  }, [queryClient]);

  return {
    playbooks,
    isLoading,
    error: null, // useQuery has error, but keeping it null for compatibility if not used
    isFetched,
    fetchPlaybooks,
    deletePlaybook: (id: string) => deletePlaybookMutation.mutate(id),
    duplicatePlaybook: (id: string) => duplicatePlaybookMutation.mutate(id),
    isDuplicating: duplicatePlaybookMutation.isPending,
    addPlaybook,
    updatePlaybook,
  };
};
